import { Parser } from '~/api';
import type { RustPermissionOpTypeValue } from '~/api/models';
import { RustPermissionGroup, RustPermissionOp, RustPermissionSettings, RustPermissionUser } from '~/api/models';
import RequestService from './request';

export interface RustPermissionCounts {
    groups: number;
    users: number;
    registeredPermissions: number;
    pendingOps: number;
    failedOps: number;
}

export interface RustPermissionsStatus {
    settings: RustPermissionSettings;
    counts: RustPermissionCounts;
}

export interface RustPermissionsMatrix {
    groups: RustPermissionGroup[];
    /** Plugin prefix ("kits") to its registered permission strings, sorted. */
    registeredPermissions: Record<string, string[]>;
    ops: RustPermissionOp[];
}

export interface EffectivePermissionSource {
    type: 'direct' | 'group';
    group?: string;
    via?: string[];
}

export interface EffectivePermission {
    permission: string;
    sources: EffectivePermissionSource[];
}

export interface RustPermissionUserDetail {
    user: RustPermissionUser;
    effective: EffectivePermission[];
}

/** Wire format for one queued change; keys match the backend op payloads. */
export interface RustPermissionOpInput {
    op: RustPermissionOpTypeValue;
    group?: string;
    permission?: string;
    steam_id?: string;
    nickname?: string | null;
    title?: string | null;
    rank?: number;
    parent?: string | null;
    auth_level?: number;
}

class RustPermissionsService {
    getStatus(): Promise<RustPermissionsStatus> {
        return RequestService.get('/servers/:server/rust-permissions').then((res) => ({
            settings: Parser.parse(res),
            counts: {
                groups: res.meta?.counts?.groups ?? 0,
                users: res.meta?.counts?.users ?? 0,
                registeredPermissions: res.meta?.counts?.registered_permissions ?? 0,
                pendingOps: res.meta?.counts?.pending_ops ?? 0,
                failedOps: res.meta?.counts?.failed_ops ?? 0,
            },
        }));
    }

    enable(): Promise<RustPermissionSettings> {
        return RequestService.post('/servers/:server/rust-permissions/enable').then(Parser.parse);
    }

    disable(): Promise<RustPermissionSettings> {
        return RequestService.post('/servers/:server/rust-permissions/disable').then(Parser.parse);
    }

    sync(): Promise<void> {
        return RequestService.post('/servers/:server/rust-permissions/sync');
    }

    getMatrix(): Promise<RustPermissionsMatrix> {
        return RequestService.get('/servers/:server/rust-permissions/matrix').then((res) => ({
            groups: Parser.parse(res.groups, true),
            registeredPermissions: res.registered_permissions ?? {},
            ops: Parser.parse(res.ops, true),
        }));
    }

    getGroups(): Promise<RustPermissionGroup[]> {
        return RequestService.get('/servers/:server/rust-permissions/groups').then((res) => Parser.parse(res, true));
    }

    getGroupMembers(groupId: string, req?: PaginatableRequest): Promise<ListResponse> {
        return RequestService.get(`/servers/:server/rust-permissions/groups/${groupId}/members`, { ...req }).then(Parser.parse);
    }

    getUsers(req?: PaginatableRequest & { group?: string }): Promise<ListResponse> {
        return RequestService.get('/servers/:server/rust-permissions/users', { ...req }).then(Parser.parse);
    }

    getUser(steamId: string): Promise<RustPermissionUserDetail> {
        return RequestService.get(`/servers/:server/rust-permissions/users/${steamId}`).then((res) => ({
            user: Parser.parse(res),
            effective: (res.meta?.effective ?? []) as EffectivePermission[],
        }));
    }

    submitOps(ops: RustPermissionOpInput[]): Promise<RustPermissionOp[]> {
        return RequestService.post('/servers/:server/rust-permissions/ops', { ops }).then((res) => Parser.parse(res, true));
    }

    getOps(req?: PaginatableRequest & { 'filter[source]'?: string; 'filter[status]'?: string }): Promise<ListResponse> {
        return RequestService.get('/servers/:server/rust-permissions/ops', { ...req }).then(Parser.parse);
    }

    cancelOp(id: string): Promise<void> {
        return RequestService.delete(`/servers/:server/rust-permissions/ops/${id}`);
    }
}

export default new RustPermissionsService();
